import { CdkTextareaAutosize, TextFieldModule } from '@angular/cdk/text-field';
import * as i0 from '@angular/core';
import { booleanAttribute, numberAttribute, Component, ChangeDetectionStrategy, ViewEncapsulation, Self, Optional, Input, ViewChild, HostListener, NgModule } from '@angular/core';
import * as i2 from '@sbb-esta/angular/core';
import { _ErrorStateTracker, SbbCommonModule } from '@sbb-esta/angular/core';
import * as i1 from '@angular/forms';
import { SbbFormFieldControl } from '@sbb-esta/angular/form-field';
import { Subject, BehaviorSubject } from 'rxjs';

// Workaround for: https://github.com/bazelbuild/rules_nodejs/issues/1265
let nextId = 0;
class SbbTextarea {
    get _labelCharactersRemaining() {
        return $localize `:Counter text for textarea@@sbbTextareaCounterText:${this._counter.value} characters remaining`;
    }
    get id() {
        return this._id;
    }
    set id(value) {
        this._id = value;
        this.stateChanges.next();
    }
    /** Id for the inner input field. */
    get inputId() {
        return `${this.id || this._uniqueId}-input`;
    }
    /** The value of the textarea. */
    get value() {
        return this._textarea ? this._textarea.nativeElement.value : '';
    }
    set value(value) {
        if (this._textarea) {
            this._textarea.nativeElement.value = value;
            this._updateDigitsCounter(this.value);
            this._changeDetectorRef.markForCheck();
            this.stateChanges.next();
        }
    }
    /** Class property that disables the textarea status. */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = value;
        this._changeDetectorRef.markForCheck();
        this.stateChanges.next();
    }
    /** Class property that sets readonly the textarea content. */
    get readonly() {
        return this._readonly;
    }
    set readonly(value) {
        this._readonly = value;
        this.stateChanges.next();
    }
    /** Whether the input is in an error state. */
    get errorState() {
        return this._errorStateTracker.errorState;
    }
    set errorState(value) {
        this._errorStateTracker.errorState = value;
    }
    /** Class property that sets the maxlength of the textarea content. */
    get maxlength() {
        return this._maxlength;
    }
    set maxlength(value) {
        this._maxlength = value;
        this._updateDigitsCounter(this.value);
        this.stateChanges.next();
    }
    /** Class property that sets the minlength of the textarea content. */
    get minlength() {
        return this._minlength;
    }
    set minlength(value) {
        this._minlength = value;
        this.stateChanges.next();
    }
    /** Class property that sets required the textarea. */
    get required() {
        return this._required;
    }
    set required(value) {
        this._required = value;
        this.stateChanges.next();
    }
    get errorStateMatcher() {
        return this._errorStateTracker.matcher;
    }
    set errorStateMatcher(value) {
        this._errorStateTracker.matcher = value;
    }
    /** Whether the textarea is focused. */
    get focused() {
        return this._focused;
    }
    /** Whether the textarea has a value. */
    get empty() {
        return !this.value || this.value === '';
    }
    constructor(ngControl, _changeDetectorRef, _elementRef, defaultErrorStateMatcher, parentForm, parentFormGroup) {
        this.ngControl = ngControl;
        this._changeDetectorRef = _changeDetectorRef;
        this._elementRef = _elementRef;
        /** Unique id of the element. */
        this._uniqueId = `sbb-textarea-${++nextId}`;
        this._id = this._uniqueId;
        /**
         * Disable the automatic labeling to avoid issues like #1918.
         * @docs-private
         */
        this.disableAutomaticLabeling = true;
        /** Emits when the state of the option changes and any parents have to be notified. */
        this.stateChanges = new Subject();
        this._destroyed = new Subject();
        this._disabled = false;
        this._readonly = false;
        this._required = false;
        /** Whether the autosizing is disabled or not. Autosizing is based on the CDK Autosize. */
        this.autosizeDisabled = false;
        this._focused = false;
        /** Class property that represents an observer on the number of digits in a textarea. */
        this._counter = new BehaviorSubject(this.maxlength);
        /** Placeholder value for the textarea. */
        this.placeholder = '';
        /** `View -> model callback called when value changes` */
        this._onChange = () => { };
        /** `View -> model callback called when autocomplete has been touched` */
        this._onTouched = () => { };
        if (this.ngControl) {
            // Note: we provide the value accessor through here, instead of
            // the `providers` to avoid running into a circular import.
            this.ngControl.valueAccessor = this;
        }
        this._errorStateTracker = new _ErrorStateTracker(defaultErrorStateMatcher, ngControl, parentFormGroup, parentForm, this.stateChanges);
    }
    /**
     * Trigger resize on every check because it's possible that the textarea becomes visible after first rendering.
     * Without triggering resize, the textarea would not be correctly adjusted when it becomes visible only after first rendering.
     * This issue is due to the fact, that before being visible, autosize is deactivated.
     * @docs-private
     */
    ngDoCheck() {
        if (this.ngControl) {
            this.updateErrorState();
        }
    }
    /**
     * Forward focus if a user clicks on an associated label.
     * Implemented as part of SbbFormFieldControl.
     * @docs-private
     */
    onContainerClick(_event) {
        this.focus();
    }
    /**
     * Note: under normal conditions focus shouldn't land on this element, however it may be
     * programmatically set, for example inside of a focus trap, in this case we want to forward
     * the focus to the native element.
     * @docs-private
     */
    focus(options) {
        this._textarea.nativeElement.focus(options);
    }
    /** Adds the focused CSS class to this element */
    _onFocus() {
        if (!this.disabled) {
            this._focused = true;
            this.stateChanges.next();
        }
    }
    /**
     * forward focus if user clicks anywhere on sbb-textarea
     * @docs-private
     */
    _focusTextarea(target) {
        if (target === this._elementRef.nativeElement) {
            this.focus();
        }
    }
    /** Removes the focused CSS class from this element */
    _onBlur() {
        this._focused = false;
        if (!this.disabled) {
            this._onTouched();
            this._changeDetectorRef.markForCheck();
            this.stateChanges.next();
        }
    }
    /** Method that listens change in the textarea content */
    _onInput(event) {
        this._onChange(event.target.value);
        this._updateDigitsCounter(event.target.value);
        this.stateChanges.next();
    }
    /** Refreshes the error state of the input. */
    updateErrorState() {
        this._errorStateTracker.updateErrorState();
    }
    writeValue(newValue) {
        this.value = newValue == null ? '' : newValue;
    }
    registerOnChange(fn) {
        this._onChange = fn;
    }
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    /**
     * Implemented as part of SbbFormFieldControl.
     * @docs-private
     */
    setDescribedByIds(ids) {
        this._ariaDescribedby = ids.join(' ');
    }
    /** @docs-private */
    ngOnDestroy() {
        this.stateChanges.complete();
        this._destroyed.next();
        this._destroyed.complete();
    }
    /** Method that updates the max number of digits available in the textarea content */
    _updateDigitsCounter(newValue) {
        if (!!this.maxlength) {
            this._counter.next(this.maxlength - newValue.length);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTextarea, deps: [{ token: i1.NgControl, optional: true, self: true }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i2.SbbErrorStateMatcher }, { token: i1.NgForm, optional: true }, { token: i1.FormGroupDirective, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.7", type: SbbTextarea, isStandalone: true, selector: "sbb-textarea", inputs: { id: "id", value: "value", disabled: ["disabled", "disabled", booleanAttribute], readonly: ["readonly", "readonly", booleanAttribute], maxlength: ["maxlength", "maxlength", numberAttribute], minlength: ["minlength", "minlength", numberAttribute], required: ["required", "required", booleanAttribute], autosizeDisabled: ["autosizeDisabled", "autosizeDisabled", booleanAttribute], errorStateMatcher: "errorStateMatcher", placeholder: "placeholder" }, host: { attributes: { "tabindex": "-1" }, listeners: { "focus": "focus()", "click": "_focusTextarea($event.target)" }, properties: { "attr.id": "id", "attr.aria-describedby": "_ariaDescribedby || null", "class.sbb-disabled": "disabled", "class.sbb-focused": "focused", "class.sbb-readonly": "readonly" }, classAttribute: "sbb-textarea sbb-input-element" }, providers: [{ provide: SbbFormFieldControl, useExisting: SbbTextarea }], viewQueries: [{ propertyName: "_textarea", first: true, predicate: ["textarea"], descendants: true, static: true }, { propertyName: "autosize", first: true, predicate: CdkTextareaAutosize, descendants: true, static: true }], ngImport: i0, template: "<textarea\n  #textarea\n  [id]=\"inputId\"\n  [disabled]=\"disabled\"\n  [readonly]=\"readonly\"\n  [placeholder]=\"!readonly ? placeholder : '-'\"\n  [attr.maxlength]=\"maxlength !== 0 ? maxlength : null\"\n  [attr.minlength]=\"minlength\"\n  [cdkTextareaAutosize]=\"!autosizeDisabled\"\n  (focus)=\"_onFocus()\"\n  (blur)=\"_onBlur()\"\n  (input)=\"_onInput($event)\"\n></textarea>\n@if (maxlength && !disabled) {\n  <div class=\"sbb-textarea-remaining-chars\">{{ _labelCharactersRemaining }}</div>\n}\n", styles: [".sbb-textarea{display:inline-block;letter-spacing:0;width:100%}.sbb-textarea textarea{width:100%;outline:none;border:none;padding:0;min-height:inherit;resize:none;overflow:hidden;background-color:rgba(0,0,0,0);text-overflow:clip}.sbb-textarea textarea:-moz-ui-invalid{box-shadow:none}.sbb-form-field-invalid .sbb-textarea textarea,.sbb-textarea.ng-touched.ng-invalid textarea{color:var(--sbb-color-error)}.sbb-textarea-remaining-chars{position:relative;float:right;background:rgba(0,0,0,0);color:var(--sbb-textarea-remaining-chars-color);letter-spacing:0;font-size:var(--sbb-font-size-small);line-height:1;font-family:var(--sbb-font-roman)}"], dependencies: [{ kind: "directive", type: CdkTextareaAutosize, selector: "textarea[cdkTextareaAutosize]", inputs: ["cdkAutosizeMinRows", "cdkAutosizeMaxRows", "cdkTextareaAutosize", "placeholder"], exportAs: ["cdkTextareaAutosize"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTextarea, decorators: [{
            type: Component,
            args: [{ selector: 'sbb-textarea', providers: [{ provide: SbbFormFieldControl, useExisting: SbbTextarea }], changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, host: {
                        class: 'sbb-textarea sbb-input-element',
                        /** Needs to be -1 so the `focus` event still fires. */
                        tabindex: '-1',
                        '[attr.id]': 'id',
                        '[attr.aria-describedby]': '_ariaDescribedby || null',
                        '[class.sbb-disabled]': 'disabled',
                        '[class.sbb-focused]': 'focused',
                        '[class.sbb-readonly]': 'readonly',
                    }, standalone: true, imports: [CdkTextareaAutosize], template: "<textarea\n  #textarea\n  [id]=\"inputId\"\n  [disabled]=\"disabled\"\n  [readonly]=\"readonly\"\n  [placeholder]=\"!readonly ? placeholder : '-'\"\n  [attr.maxlength]=\"maxlength !== 0 ? maxlength : null\"\n  [attr.minlength]=\"minlength\"\n  [cdkTextareaAutosize]=\"!autosizeDisabled\"\n  (focus)=\"_onFocus()\"\n  (blur)=\"_onBlur()\"\n  (input)=\"_onInput($event)\"\n></textarea>\n@if (maxlength && !disabled) {\n  <div class=\"sbb-textarea-remaining-chars\">{{ _labelCharactersRemaining }}</div>\n}\n", styles: [".sbb-textarea{display:inline-block;letter-spacing:0;width:100%}.sbb-textarea textarea{width:100%;outline:none;border:none;padding:0;min-height:inherit;resize:none;overflow:hidden;background-color:rgba(0,0,0,0);text-overflow:clip}.sbb-textarea textarea:-moz-ui-invalid{box-shadow:none}.sbb-form-field-invalid .sbb-textarea textarea,.sbb-textarea.ng-touched.ng-invalid textarea{color:var(--sbb-color-error)}.sbb-textarea-remaining-chars{position:relative;float:right;background:rgba(0,0,0,0);color:var(--sbb-textarea-remaining-chars-color);letter-spacing:0;font-size:var(--sbb-font-size-small);line-height:1;font-family:var(--sbb-font-roman)}"] }]
        }], ctorParameters: () => [{ type: i1.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i2.SbbErrorStateMatcher }, { type: i1.NgForm, decorators: [{
                    type: Optional
                }] }, { type: i1.FormGroupDirective, decorators: [{
                    type: Optional
                }] }], propDecorators: { id: [{
                type: Input
            }], value: [{
                type: Input
            }], disabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], readonly: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], maxlength: [{
                type: Input,
                args: [{ transform: numberAttribute }]
            }], minlength: [{
                type: Input,
                args: [{ transform: numberAttribute }]
            }], required: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], autosizeDisabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], errorStateMatcher: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], _textarea: [{
                type: ViewChild,
                args: ['textarea', { static: true }]
            }], autosize: [{
                type: ViewChild,
                args: [CdkTextareaAutosize, { static: true }]
            }], focus: [{
                type: HostListener,
                args: ['focus']
            }], _focusTextarea: [{
                type: HostListener,
                args: ['click', ['$event.target']]
            }] } });

class SbbTextareaModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTextareaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.7", ngImport: i0, type: SbbTextareaModule, imports: [TextFieldModule, SbbCommonModule, SbbTextarea], exports: [SbbTextarea] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTextareaModule, imports: [TextFieldModule, SbbCommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTextareaModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [TextFieldModule, SbbCommonModule, SbbTextarea],
                    exports: [SbbTextarea],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SbbTextarea, SbbTextareaModule };
